import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Ticket, Users, MapPin, Settings, BarChart3, TrendingUp,
  UserPlus, DollarSign, Clock, CheckCircle2, AlertCircle, ExternalLink, BellRing, Mail, Loader2, CreditCard,
  CalendarClock, Receipt, Play, AlertTriangle, ArrowRight, Copy, Link2
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { useLanguage } from "../i18n/LanguageContext";

const statusColors = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700",
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState({});
  const [tickets, setTickets] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [slaAlerts, setSlaAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [diagnosticFee, setDiagnosticFee] = useState(125);
  const [savingFee, setSavingFee] = useState(false);
  const [pendingPaymentCount, setPendingPaymentCount] = useState(0);
  const [billingData, setBillingData] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [runningBilling, setRunningBilling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, workOrdersRes, alertsRes, feeRes] = await Promise.all([
        apiClient.get("/stats/dashboard"),
        apiClient.get("/tickets"),
        apiClient.get("/work-orders"),
        apiClient.get("/sla-alerts"),
        apiClient.get("/admin/diagnostic-fee"),
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data.slice(0, 5));
      setWorkOrders(workOrdersRes.data.slice(0, 5));
      setSlaAlerts(alertsRes.data.slice(0, 5));
      setDiagnosticFee(feeRes.data.diagnostic_fee || 125);
      setPendingPaymentCount(ticketsRes.data.filter(t => t.payment_status === "pending_payment").length);

      // Fetch billing data
      try {
        const [billingRes, invoicesRes, whRes] = await Promise.all([
          apiClient.get("/admin/billing/upcoming"),
          apiClient.get("/invoices"),
          apiClient.get("/admin/webhooks/health"),
        ]);
        setBillingData(billingRes.data || []);
        setOverdueInvoices((invoicesRes.data || []).filter(inv => inv.status === "overdue" || (inv.status === "pending" && inv.due_date && inv.due_date < new Date().toISOString().split("T")[0])));
        setWebhookHealth(whRes.data?.processors || null);
      } catch { /* billing data is supplementary */ }
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestEmail = async () => {
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      const res = await apiClient.post("/admin/test-email");
      setEmailTestResult(res.data);
      if (res.data.result?.sent) {
        toast.success(`Test email sent to ${res.data.test_sent_to}`);
      } else {
        toast.error(`Email failed: ${res.data.result?.error || "Unknown error"}`);
      }
    } catch (error) {
      toast.error("Failed to run email test");
      setEmailTestResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleRunBilling = async () => {
    setRunningBilling(true);
    try {
      const res = await apiClient.post("/admin/billing/run-now");
      toast.success("Billing check completed");
      fetchData();
    } catch (error) {
      toast.error("Failed to run billing check");
    } finally {
      setRunningBilling(false);
    }
  };

  const [sendingDigest, setSendingDigest] = useState(false);
  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await apiClient.post("/admin/billing/send-digest-now");
      const counts = res.data?.result?.counts || {};
      const recipients = res.data?.result?.recipients || [];
      toast.success(`Health digest sent to ${recipients.length} recipient(s) — ${counts.overdue_invoices || 0} overdue, ${counts.drifted_contracts || 0} drifted`);
    } catch (error) {
      toast.error("Failed to send billing digest");
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSaveDiagnosticFee = async () => {
    setSavingFee(true);
    try {
      await apiClient.patch("/admin/diagnostic-fee", { diagnostic_fee: parseFloat(diagnosticFee) });
      toast.success("Diagnostic fee updated");
    } catch {
      toast.error("Failed to update diagnostic fee");
    } finally {
      setSavingFee(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, trend }) => (
    <Card className="sag-card">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          {trend && (
            <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
              <TrendingUp className="h-4 w-4" />
              {trend}
            </div>
          )}
        </div>
        <div className="mt-4">
          <p className="text-2xl font-bold font-heading text-slate-900">{value || 0}</p>
          <p className="text-sm text-slate-500 font-medium mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">Overview of your helpdesk operations</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleTestEmail}
              disabled={testingEmail}
              data-testid="test-email-button"
            >
              {testingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {testingEmail ? "Sending..." : "Test Email"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/admin/users")}
              data-testid="manage-users-button"
            >
              <Users className="h-4 w-4 mr-2" />
              Manage Users
            </Button>
            <Button 
              className="bg-sag-navy hover:bg-sag-navy-800"
              onClick={() => navigate("/admin/quickbooks")}
              data-testid="quickbooks-button"
            >
              <DollarSign className="h-4 w-4 mr-2" />
              QuickBooks
            </Button>
          </div>
        </div>

        {/* Email Test Result */}
        {emailTestResult && (
          <Card className={`sag-card ${emailTestResult.result?.sent ? "border-emerald-200" : "border-red-200"}`} data-testid="email-test-result">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {emailTestResult.result?.sent ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
                  )}
                  <div className="text-sm">
                    <p className="font-medium text-slate-900">
                      {emailTestResult.result?.sent ? "SendGrid is working" : "SendGrid failed"}
                    </p>
                    {emailTestResult.result?.sent ? (
                      <p className="text-slate-500 mt-0.5">
                        Test email sent to <strong>{emailTestResult.test_sent_to}</strong> 
                        {" "}from {emailTestResult.sender}
                        {" · "}ID: {emailTestResult.result.sendgrid_message_id}
                      </p>
                    ) : (
                      <div className="mt-0.5 space-y-1">
                        <p className="text-red-600">
                          Error: {emailTestResult.result?.error || emailTestResult.error || "Unknown"}
                        </p>
                        <p className="text-slate-500 text-xs">
                          Sender: <strong>{emailTestResult.sender || "N/A"}</strong>
                          {" · "}Portal: {emailTestResult.portal_url || "N/A"}
                        </p>
                        {emailTestResult.sendgrid_key_configured === false && (
                          <p className="text-red-500 text-xs font-medium">SENDGRID_API_KEY is not set in .env</p>
                        )}
                      </div>
                    )}
                    {emailTestResult.sendgrid_key_prefix && (
                      <p className="text-slate-400 text-xs mt-1">Key: {emailTestResult.sendgrid_key_prefix}</p>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEmailTestResult(null)}>Dismiss</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            icon={Ticket} 
            label="Total Tickets" 
            value={stats.total_tickets} 
            color="bg-sag-navy/10 text-sag-navy" 
          />
          <StatCard 
            icon={AlertCircle} 
            label="Open Tickets" 
            value={stats.open_tickets} 
            color="bg-amber-100 text-amber-600"
          />
          <StatCard 
            icon={CreditCard} 
            label="Pending Payment" 
            value={pendingPaymentCount} 
            color="bg-red-100 text-red-600"
          />
          <StatCard 
            icon={MapPin} 
            label="Work Orders" 
            value={stats.total_work_orders} 
            color="bg-blue-100 text-blue-600"
          />
          <StatCard 
            icon={Users} 
            label="Total Users" 
            value={stats.total_users} 
            color="bg-purple-100 text-purple-600"
          />
        </div>

        {/* User Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="sag-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-100 text-emerald-600">
                <UserPlus className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{stats.total_consultants || 0}</p>
                <p className="text-sm text-slate-500">Consultants</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100 text-blue-600">
                <MapPin className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{stats.total_field_agents || 0}</p>
                <p className="text-sm text-slate-500">Field Agents</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sag-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-sag-navy/10 text-sag-navy">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading text-slate-900">{stats.total_clients || 0}</p>
                <p className="text-sm text-slate-500">Clients</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Tickets */}
          <Card className="sag-card">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Recent Tickets</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")}>
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-slate-500">Loading...</div>
              ) : tickets.length === 0 ? (
                <div className="p-6 text-center text-slate-500">No tickets yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer flex items-center justify-between"
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{ticket.title}</p>
                        <p className="text-sm text-slate-500">{ticket.client_name}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {ticket.payment_status === "pending_payment" && (
                          <Badge className="bg-red-100 text-red-700 border border-red-200" variant="secondary">$</Badge>
                        )}
                        <Badge className={statusColors[ticket.status]} variant="secondary">
                          {ticket.status.replace("_", " ")}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Work Orders */}
          <Card className="sag-card">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Recent Work Orders</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/field-agent")}>
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 text-center text-slate-500">Loading...</div>
              ) : workOrders.length === 0 ? (
                <div className="p-6 text-center text-slate-500">No work orders yet</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {workOrders.map((order) => (
                    <div
                      key={order.id}
                      className="p-4 hover:bg-slate-50 transition-colors cursor-pointer flex items-center justify-between"
                      onClick={() => navigate(`/work-orders/${order.id}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{order.title}</p>
                        <p className="text-sm text-slate-500 truncate">{order.location_address}</p>
                      </div>
                      <Badge variant="secondary">
                        {order.status.replace("_", " ")}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SLA Alerts */}
        {slaAlerts.length > 0 && (
          <Card className="sag-card border-amber-200" data-testid="admin-sla-alerts">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellRing className="h-5 w-5 text-amber-600" />
                  <CardTitle className="font-heading text-lg">SLA Usage Alerts</CardTitle>
                  <Badge className="bg-red-100 text-red-700" variant="secondary">{slaAlerts.length}</Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate("/admin/sla-contracts")} data-testid="view-all-sla-alerts">
                  Manage
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {slaAlerts.map((alert) => (
                  <div key={alert.id} className="p-4 flex items-center gap-3 hover:bg-slate-50" data-testid={`admin-alert-${alert.id}`}>
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${alert.alert_type === "exhausted" ? "bg-red-500" : "bg-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {alert.client_name} — {alert.contract_title}
                      </p>
                      <p className="text-sm text-slate-500">
                        {alert.hours_used}h / {alert.total_hours}h ({alert.percent_consumed}%)
                        <span className="mx-1.5">·</span>
                        {new Date(alert.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={`${alert.alert_type === "exhausted" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} variant="secondary">
                      {alert.threshold}%
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing & Cash Flow Widget */}
        <Card className="sag-card border-sag-navy/20" data-testid="billing-cashflow-widget">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-sag-navy" />
                <CardTitle className="font-heading text-lg">Billing & Cash Flow</CardTitle>
                {webhookHealth && (
                  <div className="flex items-center gap-1.5 ml-2" data-testid="webhook-health-inline">
                    {Object.entries(webhookHealth).map(([proc, h]) => {
                      const color = h.light === "green" ? "bg-emerald-500" : h.light === "amber" ? "bg-amber-500" : "bg-red-500";
                      const last = h.last_event ? new Date(h.last_event.received_at).toLocaleString() : "never";
                      return (
                        <span
                          key={proc}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700`}
                          title={`${proc}: last webhook ${last} • ${h.received_last_24h} in 24h • ${h.errors_last_24h} errors`}
                          data-testid={`webhook-health-${proc}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${color}`} />
                          {proc}
                        </span>
                      );
                    })}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-sag-navy/10 text-sag-navy hover:bg-sag-navy/20 transition"
                          data-testid="webhook-urls-btn"
                          title="Show recommended webhook URLs for your payment processor dashboards"
                        >
                          <Link2 className="w-3 h-3" />
                          URLs
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96 p-4" data-testid="webhook-urls-popover">
                        <div className="space-y-3">
                          <div>
                            <h4 className="text-sm font-semibold text-sag-navy">Webhook URLs</h4>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Paste these into your processor dashboard. Custom domain is recommended for production.
                            </p>
                          </div>
                          {(() => {
                            const origin = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
                            const rows = [
                              { label: "Stripe", url: `${origin}/api/webhook/stripe`, events: "checkout.session.completed, payment_intent.succeeded" },
                              { label: "Fiserv IPG", url: `${origin}/api/webhook/fiserv`, events: "payment.succeeded, payment.failed" },
                            ];
                            return rows.map((r) => (
                              <div key={r.label} className="border border-slate-200 rounded-md p-2.5 bg-slate-50" data-testid={`webhook-url-row-${r.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-semibold text-slate-700">{r.label}</span>
                                  <button
                                    type="button"
                                    className="text-slate-500 hover:text-sag-navy transition"
                                    onClick={() => {
                                      navigator.clipboard.writeText(r.url);
                                      toast.success(`${r.label} webhook URL copied`);
                                    }}
                                    data-testid={`copy-webhook-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    title="Copy URL"
                                  >
                                    <Copy className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <code className="block text-[11px] font-mono text-slate-800 break-all">{r.url}</code>
                                <p className="text-[10px] text-slate-500 mt-1">Events: {r.events}</p>
                              </div>
                            ));
                          })()}
                          <p className="text-[10px] text-slate-400 leading-relaxed">
                            Tip: Your custom domain (e.g. <code>helpdesk.sebasalliance.com</code>) and any platform deployment URL both route to the same backend. Prefer the custom domain — it is stable and branded.
                          </p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                {overdueInvoices.length > 0 && (
                  <Badge className="bg-red-100 text-red-700 border border-red-200" variant="secondary" data-testid="overdue-badge">
                    {overdueInvoices.length} overdue
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRunBilling}
                  disabled={runningBilling}
                  data-testid="run-billing-now-btn"
                >
                  {runningBilling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
                  {runningBilling ? "Running..." : "Run Billing Now"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSendDigest}
                  disabled={sendingDigest}
                  data-testid="send-digest-now-btn"
                  title="Email the finance team a snapshot of upcoming billings, overdue invoices, and drifted contracts"
                >
                  {sendingDigest ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />}
                  {sendingDigest ? "Sending..." : "Send Health Digest"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate("/admin/invoices")} data-testid="view-all-invoices-btn">
                  All Invoices
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Summary Tiles */}
            {(() => {
              const totalUpcoming = billingData.reduce((sum, b) => sum + (b.fee || 0), 0);
              const overdueTotal = overdueInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0);
              const pendingInvCount = billingData.filter(b => b.pending_invoice).length;
              const nextBilling = billingData.length > 0 ? billingData[0] : null;
              return (
                <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100 border-b border-slate-100">
                  <div className="p-4 text-center" data-testid="billing-stat-recurring">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Recurring Revenue</p>
                    <p className="text-xl font-bold text-sag-navy mt-1">${totalUpcoming.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs text-slate-400">{billingData.length} active contracts</p>
                  </div>
                  <div className="p-4 text-center" data-testid="billing-stat-overdue">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Overdue</p>
                    <p className={`text-xl font-bold mt-1 ${overdueTotal > 0 ? "text-red-600" : "text-emerald-600"}`}>
                      ${overdueTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-slate-400">{overdueInvoices.length} invoice{overdueInvoices.length !== 1 ? "s" : ""}</p>
                  </div>
                  <div className="p-4 text-center" data-testid="billing-stat-pending">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Pending Invoices</p>
                    <p className="text-xl font-bold text-amber-600 mt-1">{pendingInvCount}</p>
                    <p className="text-xs text-slate-400">awaiting payment</p>
                  </div>
                  <div className="p-4 text-center" data-testid="billing-stat-next">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Next Billing</p>
                    <p className="text-sm font-bold text-slate-700 mt-1">
                      {nextBilling ? new Date(nextBilling.next_billing_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{nextBilling?.client_name || ""}</p>
                  </div>
                </div>
              );
            })()}

            {/* Overdue Invoices Alert */}
            {overdueInvoices.length > 0 && (
              <div className="bg-red-50 border-b border-red-100 px-5 py-3" data-testid="overdue-invoices-alert">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-semibold text-red-800">Overdue Invoices</span>
                </div>
                <div className="space-y-1.5">
                  {overdueInvoices.slice(0, 5).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between text-sm" data-testid={`overdue-inv-${inv.id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-red-700">{inv.invoice_number}</span>
                        <span className="text-red-800 truncate">{inv.client_name}</span>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="font-semibold text-red-700">${(inv.total || 0).toFixed(2)}</span>
                        <span className="text-xs text-red-500">due {inv.due_date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Billing Schedule */}
            <div className="divide-y divide-slate-100" data-testid="billing-schedule-list">
              {billingData.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">No active billing contracts</div>
              ) : (
                billingData.map(item => {
                  const nextDate = new Date(item.next_billing_date + "T00:00:00");
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const daysUntil = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
                  const isPast = daysUntil < 0;
                  const isSoon = daysUntil >= 0 && daysUntil <= 7;

                  return (
                    <div key={item.contract_id} className="px-5 py-3.5 hover:bg-slate-50 transition-colors flex items-center gap-4" data-testid={`billing-row-${item.contract_id}`}>
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isPast ? "bg-red-500" : isSoon ? "bg-amber-500" : "bg-emerald-500"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{item.client_name}</p>
                        <p className="text-xs text-slate-400 truncate">{item.contract_title}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-slate-700">${(item.fee || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-slate-400">{item.billing_cycle}</p>
                      </div>
                      <div className="text-right flex-shrink-0 w-24">
                        <p className={`text-xs font-medium ${isPast ? "text-red-600" : isSoon ? "text-amber-600" : "text-slate-600"}`}>
                          {nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </p>
                        <p className={`text-xs ${isPast ? "text-red-500 font-semibold" : isSoon ? "text-amber-500" : "text-slate-400"}`}>
                          {isPast ? `${Math.abs(daysUntil)}d overdue` : daysUntil === 0 ? "Today" : `in ${daysUntil}d`}
                        </p>
                      </div>
                      {item.pending_invoice && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs" variant="secondary">
                          <Receipt className="h-3 w-3 mr-1" />
                          {item.pending_invoice.status}
                        </Badge>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* Diagnostic Fee & Service Policy */}
        <Card className="sag-card" data-testid="diagnostic-fee-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-600" /> Service Policy Settings
            </CardTitle>
            <CardDescription>Configure the default diagnostic/troubleshooting fee charged to ad-hoc clients</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-xs">
                <label className="text-xs font-medium text-slate-500 uppercase mb-1 block">Diagnostic Fee (USD)</label>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-slate-600">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={diagnosticFee}
                    onChange={(e) => setDiagnosticFee(e.target.value)}
                    className="max-w-[120px]"
                    data-testid="diagnostic-fee-input"
                  />
                </div>
              </div>
              <Button 
                onClick={handleSaveDiagnosticFee} 
                disabled={savingFee} 
                className="bg-sag-navy hover:bg-sag-navy-800 mt-5"
                data-testid="save-diagnostic-fee-btn"
              >
                {savingFee ? "Saving..." : "Save"}
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-3">This fee is shown on new tickets from non-SLA clients. SLA clients are automatically exempt.</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
